/**
 * useGeneratorState — form state for the generator.
 *
 * Phase 3: the state IS a taxonomy selections object, managed by the
 * taxonomy engine so every change cascades (an industry change resets its
 * category; a document change resets anything the new document can't
 * support). The page stays free of state plumbing, and `documentType`
 * keeps its Phase 1/2 shape for the knowledge panel.
 */
import { useState } from 'react';
import { DOCUMENT_TYPES } from '../constants/documentTypes';
import { taxonomyEngine } from '../taxonomy';

/**
 * Brief fields that are not taxonomy.
 *
 * Nothing about a Region narrows the Languages on offer, and a campaign title
 * has no compatibility relationships at all — so these are held beside the
 * taxonomy selections rather than inside them, where the engine would have to
 * validate them against registries that do not exist.
 */
const EMPTY_BRAND = Object.freeze({
  region: 'Global',
  language: 'English',
  campaignTitle: '',
  brandName: '',
  logo: '',
  brandColors: {},
  contactDetails: {},
});

const EMPTY_SETTINGS = Object.freeze({
  quality: 'Balanced',
  variation: 'Balanced',
  images: 'Photography (auto source)',
});

export const useGeneratorState = () => {
  const [selections, setSelections] = useState(() =>
    taxonomyEngine.applySelection(
      taxonomyEngine.createEmptySelections(),
      'documentType',
      DOCUMENT_TYPES[0]
    )
  );
  const [brand, setBrand] = useState(EMPTY_BRAND);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);

  /** Set any taxonomy field; downstream incompatibilities reset automatically. */
  const select = (fieldId, value) =>
    setSelections((prev) => taxonomyEngine.applySelection(prev, fieldId, value));

  const setBrandField = (fieldId, value) => setBrand((prev) => ({ ...prev, [fieldId]: value }));
  const setSettingField = (fieldId, value) => setSettings((prev) => ({ ...prev, [fieldId]: value }));

  return {
    selections,
    select,
    brand,
    setBrandField,
    settings,
    setSettingField,
    // Phase 1/2 compatibility surface
    documentType: selections.documentType,
    setDocumentType: (name) => select('documentType', name),
  };
};

export { EMPTY_BRAND, EMPTY_SETTINGS };
